import type { JSX, ReactNode } from 'react';
import { LuBot, LuBotOff, LuCalendarClock, LuFileText, LuListTodo, LuVideo } from 'react-icons/lu';

import { EventView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/utils/cn';

import { EventMeetingBot, getEventTitleMarkerClassName } from './event-details-shared';
import { EventActionIconButton, EventDetailsActionBar } from './EventDetailsActionBar';
import { EventDetailsDescriptionSection } from './EventDetailsDescriptionSection';
import { EventDetailsEditNotice } from './EventDetailsEditNotice';
import { EventDetailsMeta } from './EventDetailsMeta';
import { EventDetailsNotesSection } from './EventDetailsNotesSection';
import { EventDetailsTasksSection } from './EventDetailsTasksSection';
import { type EventDetailsActionProps, useEventDetailsModel } from './useEventDetailsModel';

interface EventDetailsContentProps {
    className?: string;
    event: EventView;
    headerEndSlot?: ReactNode;
    meetingBotEnabled?: boolean;
    onRequestClose?: () => void;
    titleMarkerClassName?: string;
}

interface EventDetailsHeaderProps extends EventDetailsActionProps {
    event: EventView;
    eventDate: string;
    eventTimeRange: string;
    headerEndSlot?: ReactNode;
    meetingBot: EventMeetingBot | null;
    titleMarkerBackgroundClassName: string;
    onScheduleMeetingBot: () => void;
}

export function EventDetailsContent({
    className,
    event,
    headerEndSlot,
    meetingBotEnabled = true,
    onRequestClose,
    titleMarkerClassName,
}: EventDetailsContentProps): JSX.Element {
    const {
        canInviteMeetingBot,
        canInviteMeetingBotDisabledReason,
        canScheduleMeetingBot,
        canJoinMeeting,
        canStopMeetingBot,
        canUnscheduleMeetingBot,
        eventDate,
        eventTimeRange,
        meetingBot,
        notes,
        onCreateNote,
        onCreateTask,
        onInviteMeetingBot,
        onScheduleMeetingBot,
        onNoteClick,
        onRemoveMeetingBot,
        onTaskClick,
        tasks,
    } = useEventDetailsModel({
        event,
        meetingBotEnabled,
        onRequestClose,
    });
    const actionProps: EventDetailsActionProps = {
        canInviteMeetingBot,
        canInviteMeetingBotDisabledReason,
        canScheduleMeetingBot,
        canJoinMeeting,
        canStopMeetingBot,
        canUnscheduleMeetingBot,
        eventUrl: event.url,
        onCreateNote,
        onCreateTask,
        onInviteMeetingBot,
        onScheduleMeetingBot,
        onRemoveMeetingBot,
    };
    const titleMarkerBackgroundClassName = titleMarkerClassName ?? getEventTitleMarkerClassName(event);

    return (
        <div className={cn('space-y-6', className)}>
            <EventDetailsHeader
                {...actionProps}
                event={event}
                eventDate={eventDate}
                eventTimeRange={eventTimeRange}
                headerEndSlot={headerEndSlot}
                meetingBot={meetingBot}
                titleMarkerBackgroundClassName={titleMarkerBackgroundClassName}
            />
            <EventDetailsNotesSection notes={notes} onNoteClick={onNoteClick} />
            <EventDetailsTasksSection tasks={tasks} onTaskClick={onTaskClick} />
            <EventDetailsDescriptionSection description={event.description} eventGuid={event.eventGUID} />
            <EventDetailsEditNotice />
        </div>
    );
}

function EventDetailsHeader({
    canInviteMeetingBot,
    canInviteMeetingBotDisabledReason,
    canScheduleMeetingBot,
    canJoinMeeting,
    canStopMeetingBot,
    canUnscheduleMeetingBot,
    event,
    eventDate,
    eventTimeRange,
    eventUrl,
    headerEndSlot,
    meetingBot,
    onCreateNote,
    onCreateTask,
    onInviteMeetingBot,
    onScheduleMeetingBot,
    onRemoveMeetingBot,
    titleMarkerBackgroundClassName,
}: EventDetailsHeaderProps): JSX.Element {
    const actionProps: EventDetailsActionProps = {
        canInviteMeetingBot,
        canInviteMeetingBotDisabledReason,
        canScheduleMeetingBot,
        canJoinMeeting,
        canStopMeetingBot,
        canUnscheduleMeetingBot,
        eventUrl,
        onCreateNote,
        onCreateTask,
        onInviteMeetingBot,
        onScheduleMeetingBot,
        onRemoveMeetingBot,
    };
    const topActions = buildTopActions(actionProps);

    return (
        <div className="space-y-4">
            <EventDetailsActionBar actions={topActions} closeSlot={headerEndSlot} />
            <div className="flex items-center gap-3">
                <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                    <span
                        className={cn('h-3.5 w-3.5 rounded-full', titleMarkerBackgroundClassName)}
                        aria-hidden="true"
                    />
                </div>
                <h3 className="min-w-0 flex-1 text-lg font-semibold">{event.title}</h3>
            </div>

            <EventDetailsMeta
                event={event}
                eventDate={eventDate}
                eventTimeRange={eventTimeRange}
                meetingBot={meetingBot}
            />
        </div>
    );
}

function buildTopActions({
    canInviteMeetingBot,
    canInviteMeetingBotDisabledReason,
    canScheduleMeetingBot,
    canJoinMeeting,
    canStopMeetingBot,
    canUnscheduleMeetingBot,
    eventUrl,
    onCreateNote,
    onCreateTask,
    onInviteMeetingBot,
    onScheduleMeetingBot,
    onRemoveMeetingBot,
}: EventDetailsActionProps): ReactNode[] {
    const actions: ReactNode[] = [];

    if (canJoinMeeting && eventUrl) {
        actions.push(
            <EventActionIconButton key="join" label="Join Meeting">
                <Button variant="ghost" size="icon" className="rounded-full" asChild>
                    <a href={eventUrl} target="_blank" rel="noopener noreferrer" aria-label="Join Meeting">
                        <LuVideo className="h-4 w-4" />
                    </a>
                </Button>
            </EventActionIconButton>,
        );
    }

    if (canInviteMeetingBot) {
        actions.push(
            <EventActionIconButton key="invite-bot" label="Invite Claire™">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full"
                    aria-label="Invite Claire™"
                    onClick={onInviteMeetingBot}
                >
                    <LuBot className="h-4 w-4" />
                </Button>
            </EventActionIconButton>,
        );
    } else if (canInviteMeetingBotDisabledReason) {
        actions.push(
            <TooltipProvider key="invite-bot-disabled">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            <EventActionIconButton label="Invite Claire™">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="rounded-full opacity-40"
                                    aria-label="Invite Claire™"
                                    disabled
                                >
                                    <LuBot className="h-4 w-4" />
                                </Button>
                            </EventActionIconButton>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-64">
                        {canInviteMeetingBotDisabledReason}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>,
        );
    }

    if (canScheduleMeetingBot) {
        actions.push(
            <EventActionIconButton key="schedule-bot" label="Schedule Claire™">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full"
                    aria-label="Schedule Claire™"
                    onClick={onScheduleMeetingBot}
                >
                    <LuCalendarClock className="h-4 w-4" />
                </Button>
            </EventActionIconButton>,
        );
    }

    if (canStopMeetingBot) {
        actions.push(
            <EventActionIconButton key="stop-bot" label="Stop Recording">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full"
                    aria-label="Stop Recording"
                    onClick={onRemoveMeetingBot}
                >
                    <LuBotOff className="h-4 w-4" />
                </Button>
            </EventActionIconButton>,
        );
    }

    if (canUnscheduleMeetingBot) {
        actions.push(
            <EventActionIconButton key="unschedule-bot" label="Unschedule Meeting Bot">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full"
                    aria-label="Unschedule Meeting Bot"
                    onClick={onRemoveMeetingBot}
                >
                    <LuBotOff className="h-4 w-4" />
                </Button>
            </EventActionIconButton>,
        );
    }

    actions.push(
        <EventActionIconButton key="create-note" label="Create Note">
            <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                aria-label="Create Note"
                onClick={onCreateNote}
            >
                <LuFileText className="h-4 w-4" />
            </Button>
        </EventActionIconButton>,
        <EventActionIconButton key="create-task" label="Create Task">
            <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                aria-label="Create Task"
                onClick={onCreateTask}
            >
                <LuListTodo className="h-4 w-4" />
            </Button>
        </EventActionIconButton>,
    );

    return actions;
}
